import { Constants } from '@app/models/constants';
import { DateTime } from 'luxon';
import { ProblemDetails } from '../api-module/control-centre-api-client';
import { KeyValuePair } from './model/shared.model';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { TimeoutError, Observable, throwError } from 'rxjs';
import equal from 'fast-deep-equal/es6';

export class CommonUtilities {
    // New method because we don't care about order, and most array equality functions do.
    public static arraysEqual(previousArray: any, currentArray: any): boolean {
        if (!Array.isArray(previousArray) || !Array.isArray(currentArray)) {
            return false;
        }

        if (previousArray.length !== currentArray.length) {
            return false;
        }

        const indexOfDifferentItem = previousArray.findIndex(
            previousItem => currentArray.findIndex(currentItem => equal(previousItem, currentItem)) < 0
        );
        return indexOfDifferentItem === -1;
    }

    public static getErrorMessage(response: HttpErrorResponse | TimeoutError | ProblemDetails): Observable<never> {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(() => new Error(Constants.UnableToContactServerMessage));
        } else if (response instanceof ProblemDetails && response.detail) {
            const details = response.detail;
            console.error('ProblemDetails error: ', details);
            return throwError(() => new Error(details));
        }

        switch (response.status) {
            case HttpStatusCode.BadRequest:
                return throwError(() => new Error(Constants.BadRequestMessage));
            case HttpStatusCode.NotFound:
                console.error('Could not find resource.');
                return throwError(() => new Error(Constants.UnableToFindResourceMessage));
            case HttpStatusCode.Conflict:
                console.error('Conflict with request.');
                return throwError(() => new Error(Constants.ConflictErrorMessage));
            case HttpStatusCode.BadGateway:
            case HttpStatusCode.ServiceUnavailable:
            case HttpStatusCode.GatewayTimeout:
            default:
                // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
                console.error('Unable to contact server: ', response.status);
                return throwError(() => new Error(Constants.UnableToContactServerMessage));
        }
    }

    public static displayErrorMessage(error: unknown, fallBackMessage: string): string {
        if (error instanceof ProblemDetails) {
            return error.detail ?? fallBackMessage;
        }
        if (error instanceof Error) {
            return error.message ?? fallBackMessage;
        }
        return fallBackMessage;
    }

    public static compareStringWithAscOrDescOrder(itemA: string, itemB: string, isAsc: boolean) {
        if (isAsc) {
            return CommonUtilities.compareString(itemA, itemB, true);
        } else {
            return CommonUtilities.compareString(itemB, itemA, true);
        }
    }

    public static compareStringDateWithAscOrDescOrder(itemA: string, itemB: string, isAsc: boolean) {
        const timeA = DateTime.fromISO(itemA),
            timeB = DateTime.fromISO(itemB);

        if (isAsc) {
            // Ascending means from the oldest to the latest
            return timeA < timeB ? -1 : 1;
        } else {
            // Descending means from the latest to the oldest
            return timeA > timeB ? -1 : 1;
        }
    }

    public static stringEquals(leftString: string | undefined | null, rightString: string | undefined | null): boolean {
        return this.compareString(leftString, rightString) === 0;
    }

    /**
     * Compare two arguments and also handle the undefined scenarios for both arguments.
     * For edge case: return 0 (equal) if both undefined; an undefined argument is always larger than the not undefined argument
     * @param strA string to be compared
     * @param strB string to be compared
     * @param reverseForUndefined boolean to indicate the strA and strB are in reverse sorting order, and the undefined case need to be reversed as well
     * @returns either -1, 0, 1 with the same meaning of String.localeCompare()
     */
    public static compareString(strA: string | undefined, strB: string | undefined, reverseForUndefined?: boolean): number {
        if (!strA && !strB) {
            return 0;
        } else if (!strA) {
            return reverseForUndefined ? -1 : 1;
        } else if (!strB) {
            return reverseForUndefined ? 1 : -1;
        } else {
            return strA.toLocaleLowerCase().localeCompare(strB.toLocaleLowerCase());
        }
    }

    public static formatDateTime(dateTime: string | undefined | null, opts?: { showTime?: boolean; shortTime?: boolean }): string {
        if (dateTime === undefined || dateTime === null) {
            return '';
        }
        let format = Constants.DateFormatDateOnly;
        if (opts?.showTime) {
            if (opts?.shortTime) {
                format = Constants.DateFormatFullDatetimeNoSeconds;
            } else {
                format = Constants.DateFormatFullDatetime;
            }
        }
        const luxonDate = DateTime.fromISO(dateTime);
        return luxonDate.isValid ? luxonDate.toFormat(format) : '';
    }

    public static bytesToMegabytes(bytes: number, showDecimal = false) {
        // eslint-disable-next-line no-magic-numbers
        return showDecimal ? Math.round((bytes * 10) / (1024 * 1024)) / 10 : Math.trunc(bytes / (1024 * 1024));
    }

    public static getFileExtension(filename: string): string | undefined {
        const fileNameArray = filename.split('.');
        fileNameArray.shift();
        return fileNameArray.pop();
    }

    public static getKeyValuePairData(keyValuePairList: Array<KeyValuePair>, key: string): string {
        for (const keyValuePair of keyValuePairList) {
            if (keyValuePair.key == key) {
                return keyValuePair.value;
            }
        }

        console.error('Could not find key value pair', key);

        return `[${key}]`;
    }

    public static dateTimeIsEqualOrMoreRecent(
        leftDateTime: string | undefined | null | DateTime,
        rightDateTime: string | undefined | null | DateTime
    ): boolean {
        if (!leftDateTime) {
            return false;
        } else if (leftDateTime === rightDateTime || !rightDateTime) {
            return true;
        }

        leftDateTime = typeof leftDateTime === 'string' ? DateTime.fromISO(leftDateTime) : leftDateTime;
        rightDateTime = typeof rightDateTime === 'string' ? DateTime.fromISO(rightDateTime) : rightDateTime;

        if (leftDateTime.isValid !== rightDateTime.isValid) {
            return leftDateTime.isValid;
        }

        return leftDateTime >= rightDateTime;
    }
}
